import type { Account } from '../model/types';
import type { PublishMode } from '../../../shared/lib/publishMode';

export type AccountStatus = 'active' | 'inactive';

/**
 * Причина, по которой аккаунт считается неактивным.
 * null — аккаунт активен.
 */
export type InactiveReason = 'token_expired' | 'disabled' | null;

/**
 * Единый источник правды о статусе аккаунта.
 * Аккаунт неактивен, если выключен (isActive=false) ИЛИ если истёк access-токен.
 */
export function getInactiveReason(account: Account): InactiveReason {
  if (account.isActive === false) {
    return 'disabled';
  }
  const expiry = account.accessTokenExpiry ? new Date(account.accessTokenExpiry) : null;
  if (expiry && expiry < new Date()) {
    return 'token_expired';
  }
  return null;
}

export function getAccountStatus(account: Account): AccountStatus {
  return getInactiveReason(account) === null ? 'active' : 'inactive';
}

export function isAccountActive(account: Account): boolean {
  return getAccountStatus(account) === 'active';
}

export interface PhoneBadge { label: string; variant: 'destructive' }

/**
 * Badge for a PHONE account's health. null = no badge (graph account, or healthy).
 * Graph (OAuth) accounts are unaffected — their status stays in getInactiveReason.
 */
export function getPhoneBadge(account: Account): PhoneBadge | null {
  if (account.connectionType !== 'phone') return null;
  switch (account.phoneStatus) {
    case 'logged_out': return { label: 'Вышел', variant: 'destructive' };
    case 'banned': return { label: 'Бан', variant: 'destructive' };
    default: return null;
  }
}

export function isPhoneAccount(account: Account): boolean {
  return account.connectionType === 'phone';
}

/** Whether an account supports a given publish mode. */
export function canPublishVia(account: Account, mode: PublishMode): boolean {
  if (mode === 'phone') return account.connectionType === 'phone';
  return account.connectionType !== 'phone'; // graph/legacy accounts use the API
}
